import {
  AfterContentInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDrawerMode, MatSidenav } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import {
  faBars,
  faCaretRight,
  faChevronDown,
  faChevronRight,
  faUserAstronaut,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { NavigationItem } from '../../Models/NavigationItem';
import { OdinNavigationService } from '../../Services/odin-navigation-service';
 
import { environment as env } from 'apps/odin-ui/src/environments/environment';
import { Observable, Subject, takeUntil } from 'rxjs';
import { CognitoService, UserInformation } from '@odin/odin-authentication';
import { BrandingHelper } from '../../Services/branding-helper';
import {SettingsService} from "@odin/odin-settings";
import { NotificationInitializerService } from '@odin/odin-core';

@Component({
  standalone: false,
  selector: 'odin-page-container',
  templateUrl: './page-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./page-container.component.scss'],
})
export class PageContainerComponent implements AfterContentInit {
  @ViewChild('sidenav') sidenav!: MatSidenav;
  public bhelper = BrandingHelper;

  public authenticatedView = false;
  public ddOpen = faChevronDown;
  public ddClosed = faChevronRight;
  public ddOption = faCaretRight;
  public hasUnreadNotifications = false;

  private destroy$ = new Subject<void>();

  public get userIcon(): IconDefinition {
    return faUserAstronaut;
  }
  public get menuIcon(): IconDefinition {
    return faBars;
  }
  public mobileSidebarMode: MatDrawerMode = 'over';
  public desktopSidebarMode: MatDrawerMode = 'side';
  public burgerMenuOpen = false;
  public sidebarMode: MatDrawerMode = this.desktopSidebarMode;
  public versionNumber: string =
    env.releaseVersion == '|RELEASE-VERSION|'
      ? 'Development'
      : env.releaseVersion;
  public user_information$!: Observable<UserInformation>;

  public loggingOut = false;
  public lightMode: boolean = localStorage.getItem('themeName') !== 'dark'; // check the inverse because light is default

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    setTimeout(() => {
      const target = event.target as Window;
      this.setSidebarProperties(target.innerWidth);
    }, 0);
  }

  private isAuthRoute(url: string): boolean {
    return url.startsWith('/authentication');
  }

  private setSidebarProperties(width: number) {
    if (width > 850) {
      this.sidebarMode = this.desktopSidebarMode;
      this.sidenav?.open();
      this.burgerMenuOpen = true;
    } else {
      this.sidenav?.close();
      this.sidebarMode = this.mobileSidebarMode;
    }
  }

  constructor(
    private settingService: SettingsService,
    public navigationService: OdinNavigationService,
    private router: Router,
    private cognitoService: CognitoService,
    _notificationInitializer: NotificationInitializerService,
  ) {
    this.cognitoService.loginChanged.subscribe((loggedIn: boolean) => {
      document.title = loggedIn ? 'Monek Portal' : 'Monek Portal Login';
      if (!loggedIn && this.authenticatedView) {
        this.loggingOut = true;
      }
      this.authenticatedView = loggedIn && !this.isAuthRoute(this.router.url);
      if (!loggedIn && this.cognitoService.userLoggedIn())
        this.router.navigate(['/']);
    });

    this.authenticatedView =
      this.cognitoService.userLoggedIn() && !this.isAuthRoute(this.router.url);

    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        // The authentication pages must never render inside the site container,
        // even if a valid session is still present in localStorage.
        this.authenticatedView =
          this.cognitoService.userLoggedIn() &&
          !this.isAuthRoute(e.urlAfterRedirects);

        // Skip token refresh on the auth-callback route to avoid racing the
        // code-exchange flow with a stale/expired refresh token.
        if (e.urlAfterRedirects.includes('/auth-callback')) return;
        this.cognitoService.checkRefresh();
      }
    });
  }

  ngAfterContentInit(): void {
    setTimeout(() => {
      this.setSidebarProperties(window.innerWidth);
    }, 0);
  }

  public ToggleLightDark(): void {
    const currentTheme = localStorage.getItem('themeName');
    if (currentTheme === null || currentTheme === 'light') {
      this.lightMode = false;
      this.settingService.SetSetting('themeName', 'dark');
    } else if (currentTheme === 'dark') {
      this.lightMode = true;
      localStorage.setItem('themeName', 'light');
      this.settingService.SetSetting('themeName', 'light');
    }
  }

  public navigate(navigationItem: NavigationItem): void {
    if (
      navigationItem.advanced !== undefined &&
      navigationItem.advanced.subRoutes !== undefined
    ) {
      navigationItem.advanced.opened = !navigationItem.advanced.opened;
      return;
    }

    if (
      navigationItem.advanced != null &&
      navigationItem.advanced.action != null
    )
      navigationItem.advanced.action();

    if (navigationItem.route != '')
      this.router.navigate([navigationItem.route]).then(() => {
        if (screen.width < 850) {
          this.sidenav.close();
        }
      });
  }
}
